type RequiredRole = UserPermission | 'public'

interface RoutePermission {
    // Exact path, or a prefix when it ends in `/**`
    path: string,
    methods?: string[],
    role: RequiredRole,
}

// First match wins. API routes that aren't listed are denied; pages that aren't listed need a signed-in user.
const ROUTE_PERMISSIONS: RoutePermission[] = [
    { path: '/login', role: 'public' },
    { path: '/no-access', role: 'public' },
    { path: '/api/auth/slack/**', role: 'public' },
    { path: '/api/_auth/session', role: 'public' },
    // Authenticated by a node's ingest key inside the handler; OPTIONS is the CORS preflight from websites
    { path: '/api/ingest', methods: ['POST', 'OPTIONS'], role: 'public' },
    // The old error service (v1) passing its errors through, authenticated with the legacy ingest key inside the handler
    { path: '/api/ingest/legacy', methods: ['POST'], role: 'public' },
    // The desktop notifier: pairing is started and polled before the app has a token, everything else is
    // authenticated with the device token inside the handler (requireDesktopDevice)
    { path: '/api/desktop/pair/start', methods: ['POST'], role: 'public' },
    { path: '/api/desktop/pair/poll', methods: ['POST'], role: 'public' },
    { path: '/api/desktop/stream', methods: ['GET'], role: 'public' },
    { path: '/api/desktop/ack', methods: ['POST'], role: 'public' },
    { path: '/api/desktop/mute', methods: ['POST'], role: 'public' },
    { path: '/api/desktop/test', methods: ['POST'], role: 'public' },
    { path: '/api/desktop/unpair', methods: ['POST'], role: 'public' },
    // The error collector on exhibit machines: pairing is started and polled before it has a key, check-ins are
    // authenticated with the collector's own ingest key inside the handler (requireCollectorDevice), like unpairing
    { path: '/api/collector/pair/start', methods: ['POST'], role: 'public' },
    { path: '/api/collector/pair/poll', methods: ['POST'], role: 'public' },
    { path: '/api/collector/checkin', methods: ['POST'], role: 'public' },
    { path: '/api/collector/unpair', methods: ['POST'], role: 'public' },
    // Desktop app releases: uploaded and published by CI with the release upload token, checked inside the handler
    { path: '/api/desktop/releases', methods: ['POST'], role: 'public' },
    { path: '/api/desktop/releases/**', methods: ['POST'], role: 'public' },
    // The desktop app's updater and first-install downloads can't sign in
    { path: '/desktop/update/**', methods: ['GET', 'HEAD'], role: 'public' },
    { path: '/desktop/files/**', methods: ['GET', 'HEAD'], role: 'public' },
    { path: '/desktop/download/**', methods: ['GET', 'HEAD'], role: 'public' },
    // Collector releases: uploaded by its CI with its own upload token, checked inside the handler. Publishing is
    // admin only (below). The collector's updater and first-install downloads can't sign in.
    { path: '/api/collector/releases', methods: ['POST'], role: 'public' },
    { path: '/collector/update/**', methods: ['GET', 'HEAD'], role: 'public' },
    { path: '/collector/files/**', methods: ['GET', 'HEAD'], role: 'public' },
    { path: '/collector/download/**', methods: ['GET', 'HEAD'], role: 'public' },
    // Installation page and commands can be handed to someone at a target machine without giving them an account
    { path: '/collector', methods: ['GET'], role: 'public' },

    { path: '/api/users/get-self', role: UserPermission.user },
    { path: '/api/users/updateSelf', methods: ['POST'], role: UserPermission.user },
    { path: '/api/users/generate_new_token', methods: ['POST'], role: UserPermission.user },
    { path: '/api/users', methods: ['GET', 'POST'], role: UserPermission.admin },
    { path: '/api/users/**', methods: ['GET', 'PATCH', 'DELETE'], role: UserPermission.admin },

    // Everyone can view the project structure, only admins can change it or get ingest keys
    { path: '/api/collections', methods: ['GET'], role: UserPermission.user },
    { path: '/api/collections', methods: ['POST'], role: UserPermission.admin },
    { path: '/api/collections/**', methods: ['GET'], role: UserPermission.user },
    { path: '/api/collections/**', methods: ['PATCH', 'DELETE'], role: UserPermission.admin },
    { path: '/api/groups', methods: ['POST'], role: UserPermission.admin },
    { path: '/api/groups/**', methods: ['PATCH', 'DELETE'], role: UserPermission.admin },
    { path: '/api/nodes', methods: ['POST'], role: UserPermission.admin },
    { path: '/api/nodes/**', methods: ['GET'], role: UserPermission.user },
    { path: '/api/nodes/**', methods: ['POST', 'PATCH', 'DELETE'], role: UserPermission.admin },

    // Error data. Triaging (changing an issue's status) is day-to-day work, so every user can do it.
    { path: '/api/issues', methods: ['GET'], role: UserPermission.user },
    { path: '/api/issues/**', methods: ['GET', 'PATCH'], role: UserPermission.user },
    { path: '/api/events/**', methods: ['GET'], role: UserPermission.user },

    // Signed-in users confirm pairing codes, manage their own devices, choose what they follow and how they're notified
    { path: '/api/desktop/pair/**', methods: ['GET', 'POST'], role: UserPermission.user },
    { path: '/api/desktop/devices', methods: ['GET'], role: UserPermission.user },
    { path: '/api/desktop/devices/test', methods: ['POST'], role: UserPermission.user },
    { path: '/api/desktop/devices/**', methods: ['DELETE'], role: UserPermission.user },
    { path: '/api/subscriptions', methods: ['GET', 'POST'], role: UserPermission.user },
    { path: '/api/subscriptions/**', methods: ['PATCH', 'DELETE'], role: UserPermission.user },
    { path: '/api/notification-settings', methods: ['GET', 'PATCH'], role: UserPermission.user },
    // The activity feed of what the user follows
    { path: '/api/activity', methods: ['GET'], role: UserPermission.user },

    { path: '/api/maintenance/cleanup', methods: ['POST'], role: UserPermission.admin },
    // Overview of desktop app releases; uploading happens with the upload token
    { path: '/api/desktop/releases', methods: ['GET'], role: UserPermission.admin },
    // Publishing from /admin/releases (CI can also publish with the upload token)
    { path: '/api/admin/desktop-releases/**', methods: ['POST'], role: UserPermission.admin },
    // Collector releases: the overview, and publishing (only from /admin/releases, with a session: not the upload token)
    { path: '/api/collector/releases', methods: ['GET'], role: UserPermission.admin },
    { path: '/api/collector/releases/**', methods: ['POST'], role: UserPermission.admin },

    // Admins approve collectors waiting to be paired and pick their node, and manage paired collectors.
    // Everyone sees which collectors are paired to a node, next to its errors.
    { path: '/api/collector/pair', methods: ['GET'], role: UserPermission.admin },
    { path: '/api/collector/pair/**', methods: ['GET', 'POST'], role: UserPermission.admin },
    { path: '/api/collector/devices', methods: ['GET'], role: UserPermission.user },
    { path: '/api/collector/devices/**', methods: ['PATCH', 'DELETE'], role: UserPermission.admin },
    { path: '/collector/pair', role: UserPermission.admin },

    { path: '/admin', role: UserPermission.admin },
    { path: '/admin/**', role: UserPermission.admin },
]

const matchesPath = (pattern: string, path: string) => {
    if (!pattern.endsWith('/**')) return path === pattern
    const prefix = pattern.slice(0, -3)
    return path.startsWith(`${ prefix }/`)
}

export function getRequiredRole(path: string, method: string): RequiredRole | null {
    const normalized = path.length > 1 ? path.replace(/\/+$/, '') : path
    const rule = ROUTE_PERMISSIONS.find(r => matchesPath(r.path, normalized) && (!r.methods || r.methods.includes(method.toUpperCase())))
    if (rule) return rule.role

    const isApiRequest = normalized === '/api' || normalized.startsWith('/api/')
    return isApiRequest ? null : UserPermission.user
}
